import { Component, DestroyRef, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { LayoutService } from './service/app.layout.service';
import { LocalStorageService } from '../core/service/local-storage.service';
import { LocalStorageEnum } from '../shared/enum.ts/local-storage.enum';
import { AuthService } from '../core/service/auth.service';
import { Role } from '../shared/enum.ts/role.enum';
import { AppMenuitemComponent } from './app.menuitem.component';

@Component({
  selector: 'app-menu',
  standalone: true,
  imports: [CommonModule, AppMenuitemComponent],
  templateUrl: './app.menu.component.html',
})
export class AppMenuComponent implements OnInit {
  readonly layoutService = inject(LayoutService);
  private readonly localStorage = inject(LocalStorageService);
  private readonly authService = inject(AuthService);
  private readonly destroyRef = inject(DestroyRef);

  model: any[] = [];
  /** Current session role (JWT / login payload); compare with {@link Role}. */
  role: Role | '' = '';

  ngOnInit(): void {
    const user = this.localStorage.getItem(LocalStorageEnum.USER) as { roleName?: string } | null;
    const roleName = user?.roleName as Role | undefined;
    this.role = roleName ?? '';
    let items = [];
    if (this.role === Role.SUPER_ADMIN) {
      items = superAdmin;
    } else if (this.role === Role.STORE_ADMIN) {
      items = storeAdmin;
    } else if (this.role === Role.MANAGER) {
      items = manager;
    }
    this.model = [
      {
        label: 'Home',
        items: [
          ...items,
          {
            label: 'Sign Out',
            icon: 'pi pi-fw pi-user',
            command: () => this.signOut(),
          },
        ],
      },
    ];
  }

  handleClick(item: any): void {
    if (item.click) {
      item.click();
    }
  }

  private signOut(): void {
    this.authService
      .logout()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => this.authService.clearSession(),
        error: () => this.authService.clearSession(),
      });
  }
}

/** Shared nav for store admin and manager (manager omits the managers-only entry). */
const sharedStoreStaffMenu = [
  { label: 'Dashboard', icon: 'pi pi-fw pi-home', routerLink: ['/'] },
  { label: 'Menu Item', icon: 'pi pi-fw pi-table', routerLink: ['/menu-item'] },
  { label: 'Orders', icon: 'pi pi-fw pi-shopping-bag', routerLink: ['/order'] },
  { label: 'Store', icon: 'pi pi-fw pi-building', routerLink: ['/store'] },
  { label: 'Loyalty Points Ledger', icon: 'pi pi-fw pi-book', routerLink: ['/loyalty-points-ledger'] },
  { label: 'Inventory Item', icon: 'pi pi-fw pi-shopping-cart', routerLink: ['/inventory-item'] },
  { label: 'Store Inventory', icon: 'pi pi-fw pi-book', routerLink: ['/store-inventory'] },
  { label: 'Notification', icon: 'pi pi-fw pi-bookmark-fill', routerLink: ['/notifications'] },
  { label: 'Settings', icon: 'pi pi-fw pi-cog', routerLink: ['/settings'] },
];

const manager = [...sharedStoreStaffMenu];

const storeAdmin = [
  sharedStoreStaffMenu[0],
  { label: 'Managers', icon: 'pi pi-fw pi-users', routerLink: ['/user'] },
  ...sharedStoreStaffMenu.slice(1),
];
const superAdmin = [
  { label: 'Dashboard', icon: 'pi pi-fw pi-home', routerLink: ['/'] },
  {
    label: 'Users',
    icon: 'pi pi-fw pi-user',
    items: [
      { label: 'All users', icon: 'pi pi-fw pi-users', routerLink: ['/user'] },
      { label: 'Soft-deleted users', icon: 'pi pi-fw pi-trash', routerLink: ['/user/soft-deleted-users'] },
    ],
  },
  { label: 'Customers', icon: 'pi pi-fw pi-users', routerLink: ['/customer'] },
  { label: 'Category', icon: 'pi pi-fw pi-th-large', routerLink: ['/category'] },
  { label: 'Dish', icon: 'pi pi-fw pi-list', routerLink: ['/dish'] },
  { label: 'Menu Item', icon: 'pi pi-fw pi-table', routerLink: ['/menu-item'] },
  { label: 'Popular Items', icon: 'pi pi-fw pi-star', routerLink: ['/popular-menu-item'] },
  { label: 'Side Lines', icon: 'pi pi-fw pi-list', routerLink: ['/side-line'] },
  { label: 'Orders', icon: 'pi pi-fw pi-shopping-bag', routerLink: ['/order'] },
  { label: 'Store', icon: 'pi pi-fw pi-building', routerLink: ['/store'] },
  { label: 'Inventory Item', icon: 'pi pi-fw pi-shopping-cart', routerLink: ['/inventory-item'] },
  { label: 'Store Inventory', icon: 'pi pi-fw pi-book', routerLink: ['/store-inventory'] },
  { label: 'Promo Codes', icon: 'pi pi-fw pi-ticket', routerLink: ['/promo-code'] },
  { label: 'Notification', icon: 'pi pi-fw pi-bookmark-fill', routerLink: ['/notifications'] },
  { label: 'Loyalty Program', icon: 'pi pi-fw pi-bookmark-fill', routerLink: ['/loyalty-program'] },
  { label: 'Loyalty Points Ledger', icon: 'pi pi-fw pi-book', routerLink: ['/loyalty-points-ledger'] },
  { label: 'Settings', icon: 'pi pi-fw pi-cog', routerLink: ['/settings'] },
];
